<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const toast = useToast();

const BASE_ROLES = ['superadmin', 'admin', 'user'];

const items = computed(() => adminStore.roles);

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());

function defaultForm() {
    return {
        name: "",
        description: "",
        type: "custom",
        category: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
}

function isBaseRole(item) {
    return BASE_ROLES.includes(item?.name);
}

function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        name: item.name || "",
        description: item.description || "",
        type: item.type || "custom",
        category: item.category || "",
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    if (!editing.value?.id && !form.name.trim()) {
        toast.error("Укажите имя роли");
        return;
    }
    const payload = {
        description: form.description,
        type: form.type,
        category: form.category,
    };
    const isEdit = !!editing.value?.id;
    const data = isEdit
        ? await adminStore.updateRole(editing.value.id, payload)
        : await adminStore.createRole({name: form.name.trim(), ...payload});
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (data) {
        toast.success(isEdit ? "Роль обновлена" : "Роль создана");
        cancelForm();
        await adminStore.loadRoles();
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить роль "${item.name}"?`)) return;
    const ok = await adminStore.deleteRole(item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Роль удалена");
        await adminStore.loadRoles();
    }
}

onMounted(() => {
    adminStore.loadRoles();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Роли</div>
      <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
        <template #caption>Добавить роль</template>
      </ActionButton>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование роли' : 'Новая роль'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <label class="adminCrud__label">Имя</label>
          <form-input v-model="form.name" :disabled="!!editing?.id"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Тип</label>
          <select v-model="form.type" class="adminCrud__select">
            <option value="custom">custom</option>
            <option value="system">system</option>
          </select>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Категория</label>
          <form-input v-model="form.category"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Описание</label>
          <form-input v-model="form.description" rows="3"/>
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <table v-else-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>Имя</th>
        <th>Действия</th>
        <th>Описание</th>
        <th>Тип</th>
        <th>Категория</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.name || '—' }}</td>
        <td class="adminCrud__actions">
          <button
              v-if="!isBaseRole(item)"
              class="adminCrud__actionButton"
              title="Изменить"
              @click="openEdit(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
              v-if="!isBaseRole(item)"
              class="adminCrud__actionButton adminCrud__actionButton--danger"
              title="Удалить"
              @click="deleteHandler(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <span v-if="isBaseRole(item)" class="adminCrud__baseBadge">базовая</span>
        </td>
        <td>{{ item.description || '—' }}</td>
        <td>{{ item.type || '—' }}</td>
        <td>{{ item.category || '—' }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__baseBadge {
    font-size: 12px;
    color: var(--cd-text-muted);
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
